import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Pencil, X } from "lucide-react";

const empty = { name: "", role: "", quote: "", program_key: "sat_master", photo_url: "", rating: 5, language: "de", is_published: true, sort_order: 0 };
const PROGRAMS = [
  { v: "sat_master", l: "SAT Master" },
  { v: "sat_mentor", l: "SAT Mentor" },
  { v: "sat_business", l: "SAT Business" },
  { v: "global", l: "Übergreifend" },
];

const TestimonialsAdmin = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { nav("/admin/login"); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const reload = async () => {
    const r = await api.get("/admin/testimonials");
    setItems(r.data);
  };

  const set = (k) => (e) => {
    const isCheckbox = e && e.target && e.target.type === "checkbox";
    const val = isCheckbox ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [k]: val }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, rating: Number(form.rating), sort_order: Number(form.sort_order) };
      if (editing) {
        await api.put(`/admin/testimonials/${editing}`, payload);
      } else {
        await api.post("/admin/testimonials", payload);
      }
      setEditing(null); setForm(empty);
      await reload();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Diese Stimme wirklich löschen?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    await reload();
  };

  const editOne = (t) => {
    setEditing(t.id);
    setForm({ ...empty, ...t });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading || !user) return <div className="pt-32 text-center text-white/60">Lade…</div>;

  return (
    <div className="pt-24 min-h-screen" data-testid="testimonials-admin">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
        <Link to="/admin" className="btn-ghost text-sm"><ArrowLeft size={14} /> Admin</Link>
        <h1 className="headline-xl text-4xl mt-3">Testimonials · CMS</h1>
        <p className="serif italic text-white/55 mt-2">Stimmen deiner Klient:innen — der stärkste Conversion-Hebel auf den Premium-Sales-Pages.</p>

        {/* Form */}
        <div className="mt-8 glass rounded-2xl p-6 space-y-3" data-testid="testimonial-form">
          <div className="flex items-center justify-between">
            <div className="overline">{editing ? "Bearbeiten" : "Neu erstellen"}</div>
            {editing && <button onClick={() => { setEditing(null); setForm(empty); }} className="btn-ghost text-xs"><X size={12} /> Abbrechen</button>}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input className="sat-input" placeholder="Name (z. B. Lara M.)" value={form.name} onChange={set("name")} data-testid="t-name" />
            <input className="sat-input" placeholder="Rolle (z. B. Unternehmerin · Zürich)" value={form.role} onChange={set("role")} />
          </div>
          <textarea rows={4} className="sat-input" placeholder="Zitat — was hat sich für diese Person verändert?" value={form.quote} onChange={set("quote")} data-testid="t-quote" />
          <div className="grid md:grid-cols-4 gap-3">
            <select className="sat-input" value={form.program_key} onChange={set("program_key")}>
              {PROGRAMS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
            <select className="sat-input" value={form.language} onChange={set("language")}>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
            <input type="number" min={1} max={5} className="sat-input" placeholder="Rating 1-5" value={form.rating} onChange={set("rating")} />
            <input type="number" className="sat-input" placeholder="Sort" value={form.sort_order} onChange={set("sort_order")} />
          </div>
          <input className="sat-input" placeholder="Foto-URL (optional — leer = Initiale)" value={form.photo_url} onChange={set("photo_url")} />
          <label className="flex items-center gap-2 text-sm text-white/75"><input type="checkbox" checked={form.is_published} onChange={set("is_published")} /> Veröffentlicht</label>
          <button onClick={submit} disabled={busy || !form.name || !form.quote} className="btn-primary text-sm" data-testid="t-save">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {editing ? "Speichern" : "Hinzufügen"}
          </button>
        </div>

        {/* List */}
        <div className="mt-10">
          <div className="overline mb-3">Alle Stimmen ({items.length})</div>
          <div className="grid md:grid-cols-2 gap-4" data-testid="testimonials-list">
            {items.map((t) => (
              <div key={t.id} className="rounded-xl border border-white/8 p-5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-3 text-xs">
                  <span className="overline text-[#D4AF37]">{t.program_key} · {t.language}</span>
                  <span className={`px-2 py-0.5 rounded-full ${t.is_published ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/40"}`}>{t.is_published ? "live" : "draft"}</span>
                </div>
                <div className="serif text-base mb-1">{t.name}</div>
                <div className="text-xs text-white/45 mb-3">{t.role}</div>
                <p className="text-white/75 text-sm leading-relaxed line-clamp-4">{t.quote}</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => editOne(t)} className="btn-ghost text-xs"><Pencil size={12} /> Bearbeiten</button>
                  <button onClick={() => remove(t.id)} className="btn-ghost text-xs text-red-300"><Trash2 size={12} /> Löschen</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsAdmin;
